/**
 * Phone notifications: the Settings section, the banner that asks, the silent re-sync on
 * open, and following a tapped notification inside an app that is already open. Two
 * kinds: entries someone else adds that you are on (`push_entries` — every one by
 * default, the first each day, or none) and the weekly summary (`push_digest`). A device
 * only gets either after its owner taps "Turn on" — see `lib/push.ts`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { ApiError, api, type Me } from "../../lib/api";
import {
  PushDenied,
  PushDismissed,
  howToAllow,
  pushState,
  pushSupported,
  resync,
  turnOff,
  turnOn,
  type PushConfig,
  type PushState,
} from "../../lib/push";
import { useSession } from "./session";

/** "Not now" on this device, for good. Renamed when the banner changed, so it asks once more. */
const DISMISSED = "push-banner-closed";

type EntryAlerts = NonNullable<Me["user"]["push_entries"]>;

/** The choices for entries someone else adds, and what each means in a line. */
const ENTRY_ALERTS: { value: EntryAlerts; label: string; hint: string }[] = [
  { value: "every", label: "Every entry", hint: "Each one gets its own notification." },
  {
    value: "first",
    label: "Only the first each day",
    hint: "Later ones that day stay quiet; the weekly summary has them all.",
  },
  { value: "off", label: "Never", hint: "" },
];

function usePushState() {
  const [state, setState] = useState<PushState | null>(null);
  const refresh = () => void pushState().then(setState).catch(() => setState("unsupported"));
  useEffect(refresh, []);
  return { state, refresh };
}

function usePushConfig() {
  return useQuery({
    queryKey: ["push-config"],
    queryFn: () => api.get<PushConfig>("/push/config"),
    staleTime: 60_000,
  });
}

function message(cause: unknown): string {
  if (cause instanceof PushDenied) {
    return `Notifications are blocked for The Sheet in this browser. ${howToAllow()}`;
  }
  if (cause instanceof PushDismissed) {
    return "The browser's question was closed without an answer. Tap Turn on to be asked again; if nothing appears, look for a bell in the address bar.";
  }
  if (cause instanceof ApiError) return cause.message;
  return cause instanceof Error ? cause.message : "That did not work.";
}

/** Settings → Phone notifications. */
export function PushSection() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const config = usePushConfig();
  const { state, refresh } = usePushState();
  const entryAlertsId = useId();

  const enable = useMutation({
    mutationFn: turnOn,
    onSettled: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ["push-config"] });
    },
  });
  const disable = useMutation({
    mutationFn: turnOff,
    onSettled: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ["push-config"] });
    },
  });
  const test = useMutation({ mutationFn: () => api.post<{ sent: number }>("/push/test") });
  const preferences = useMutation({
    mutationFn: (change: { push_entries?: EntryAlerts; push_digest?: boolean }) =>
      api.patch<Me>("/auth/preferences", change),
    onSuccess: (me) => queryClient.setQueryData(["me"], me),
  });

  if (!config.data?.available || state === null) return null;

  const others = Math.max(0, (config.data.devices ?? 0) - (state === "on" ? 1 : 0));
  const entryAlerts = user?.push_entries ?? "every";
  const entryHint = ENTRY_ALERTS.find((option) => option.value === entryAlerts)?.hint;

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">Phone notifications</h2>

      {state === "unsupported" ? (
        <p className="text-sm text-muted">This browser cannot show notifications.</p>
      ) : state === "install-first" ? (
        <p className="text-sm">
          To get notifications on this iPhone, add The Sheet to your Home Screen first: tap
          Share, then <strong>Add to Home Screen</strong>, and open it from there.
        </p>
      ) : state === "denied" ? (
        <div className="flex flex-col gap-1.5 text-sm">
          <p>
            Notifications are blocked for The Sheet in this browser. A website cannot ask
            again once that is set; only the browser can undo it.
          </p>
          <p>
            <strong>{howToAllow()}</strong> Then come back here and tap Turn on.
          </p>
        </div>
      ) : state === "off" ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm">
            Get notified on this device whenever someone else adds an entry you're on, and a
            summary once a week.
          </p>
          <Button variant="primary" disabled={enable.isPending} onClick={() => enable.mutate()}>
            {enable.isPending ? "Turning on…" : "Turn on notifications"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-owed">On for this device.</p>
          <div className="flex flex-col gap-1.5 text-sm">
            <label htmlFor={entryAlertsId}>When someone else adds an entry you're on</label>
            <select
              id={entryAlertsId}
              aria-describedby={entryHint ? `${entryAlertsId}-hint` : undefined}
              className="field sm:max-w-xs"
              value={entryAlerts}
              disabled={preferences.isPending}
              onChange={(event) =>
                preferences.mutate({ push_entries: event.target.value as EntryAlerts })
              }
            >
              {ENTRY_ALERTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {entryHint ? (
              <p id={`${entryAlertsId}-hint`} className="text-xs text-muted">
                {entryHint}
              </p>
            ) : null}
          </div>
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--accent))]"
              checked={user?.push_digest ?? true}
              disabled={preferences.isPending}
              onChange={(event) => preferences.mutate({ push_digest: event.target.checked })}
            />
            <span>
              A weekly summary.{" "}
              <span className="text-muted">Nothing is sent in a quiet week.</span>
            </span>
          </label>
          <p className="text-xs text-muted">
            These apply to every device you turn notifications on for. Signing out of a device
            stops its notifications until you sign in there again; if a sign-in simply runs
            out, that device is told only that something is waiting.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={test.isPending} onClick={() => test.mutate()}>
              {test.isSuccess ? "Sent — check your notifications" : "Send a test"}
            </Button>
            <Button size="sm" variant="ghost" disabled={disable.isPending} onClick={() => disable.mutate()}>
              Turn off on this device
            </Button>
          </div>
        </div>
      )}

      {others > 0 ? (
        <p className="mt-2 text-xs text-muted">
          Also on for {others} other {others === 1 ? "device" : "devices"}.
        </p>
      ) : null}
      {enable.isError || disable.isError || test.isError ? (
        <p role="alert" className="mt-2 text-sm text-owing">
          {message(enable.error ?? disable.error ?? test.error)}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The ask, at the top of the page after signing in, on every device where notifications
 * are not on yet — until it is answered here, or dismissed for good with "Not now".
 * Turning them on needs a tap: that is the browser's rule, and no app can skip it.
 */
export function PushBanner() {
  const { user } = useSession();
  const config = usePushConfig();
  const { state, refresh } = usePushState();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISSED) === "1";
    } catch {
      return false;
    }
  });
  const [after, setAfter] = useState<"later" | "on" | null>(null);
  const enable = useMutation({
    mutationFn: turnOn,
    onSuccess: () => setAfter("on"),
    onSettled: refresh,
  });

  // The follow-up line goes away by itself.
  useEffect(() => {
    if (after === null) return;
    const timer = window.setTimeout(() => setAfter(null), 10_000);
    return () => window.clearTimeout(timer);
  }, [after]);

  if (after) {
    return (
      <div
        role="status"
        className="surface mb-4 mt-3 flex items-center gap-3 px-4 py-2.5 text-sm animate-fade-in"
      >
        <p className="min-w-0 flex-1">
          {after === "on" ? "Notifications are on for this device. " : "You can turn notifications on any time in "}
          <Link to="/settings" className="text-accent underline underline-offset-2">
            {after === "on" ? "Choose which in Settings" : "Settings"}
          </Link>
          .
        </p>
        <button
          type="button"
          aria-label="Close"
          className="tap shrink-0 px-2 text-muted hover:text-ink"
          onClick={() => setAfter(null)}
        >
          ✕
        </button>
      </div>
    );
  }

  if (dismissed || !user || !config.data?.available) return null;
  if (user.push_entries === "off" && !user.push_digest) return null;
  if (state !== "off" && state !== "install-first" && state !== "denied") return null;

  const dismiss = () => {
    setDismissed(true);
    setAfter("later");
    try {
      window.localStorage.setItem(DISMISSED, "1");
    } catch {
      /* private mode: it will ask again next time, which is fine */
    }
  };

  return (
    <div className="surface mb-4 mt-3 flex flex-col gap-3 px-4 py-3 text-sm sm:flex-row sm:items-center">
      <p className="min-w-0 flex-1">
        {state === "install-first"
          ? "Add The Sheet to your Home Screen (Share → Add to Home Screen) and open it from there to get notifications when someone adds an entry for you."
          : state === "denied"
            ? `Notifications are blocked for The Sheet in this browser. ${howToAllow()}`
            : "Get a notification when someone adds an entry for you, and a weekly summary?"}
        {enable.isError ? <span className="mt-1 block text-owing">{message(enable.error)}</span> : null}
      </p>
      <div className="flex shrink-0 gap-2">
        {state === "off" ? (
          <Button variant="primary" size="sm" disabled={enable.isPending} onClick={() => enable.mutate()}>
            {enable.isPending ? "Turning on…" : "Turn on"}
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" onClick={dismiss}>
          Not now
        </Button>
      </div>
    </div>
  );
}

/**
 * Follows a tapped notification inside an app that is already open. The service worker
 * (`public/push-sw.js`) asks here first, because routing in place is instant and keeps
 * the page; it reloads the window itself only when nothing answers. Renders nothing.
 */
export function PushNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!pushSupported()) return;
    const container = navigator.serviceWorker;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; url?: unknown } | null;
      if (data?.type !== "open" || typeof data.url !== "string") return;
      // Only somewhere inside this app: "/g/1?from=…", never "//elsewhere.example".
      if (!data.url.startsWith("/") || data.url.startsWith("//")) return;
      navigate(data.url);
      event.ports[0]?.postMessage("opened");
    };
    container.addEventListener("message", onMessage);
    // A page's messages wait until it says it is listening.
    container.startMessages();
    return () => container.removeEventListener("message", onMessage);
  }, [navigate]);
  return null;
}

/** Keeps this device's subscription current on the server. Renders nothing; never asks. */
export function PushResync() {
  const { user } = useSession();
  useEffect(() => {
    if (user && pushSupported()) void resync().catch(() => undefined);
  }, [user]);
  return null;
}
